import classNames from 'classnames';
import { FC, Fragment, useCallback, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import Button from '../../button';
import Modal from '../../modal';

// This is a custom graphic specifically for this component
const SignatureIconGraphic: FC<{ className?: string }> = ({ className }) => {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      width="100%"
      height="100%"
      fill="none"
      viewBox="0 0 40 40"
      preserveAspectRatio="xMidYMid meet"
    >
      <g
        transform="translate(0.000000,40.000000) scale(0.100000,-0.100000)"
        fill="currentColor"
        stroke="none"
      >
        <path
          d="M281 369 l-24 -9 23 -25 c28 -30 40 -32 40 -6 0 11 5 23 10 26 13 8
3 25 -13 24 -7 0 -23 -5 -36 -10z"
        />

        <path
          d="M183 264 c-78 -88 -80 -92 -54 -118 l22 -21 68 75 c38 41 72 72 77
67 4 -4 -2 -17 -14 -30 -39 -41 -23 -42 18 -2 43 42 51 60 21 50 -10 -3 -24 1
-31 10 -34 41 -46 38 -107 -31z"
        />

        <path
          d="M83 123 c-3 -10 -8 -21 -10 -25 -7 -13 22 -9 40 5 16 11 16 13 -3 25
-17 11 -21 10 -27 -5z"
        />

        <path
          d="M211 59 c-21 -29 -23 -29 -36 -12 -12 16 -17 16 -46 6 -22 -9 -36 -9
-45 -2 -8 6 -14 7 -18 0 -12 -18 30 -32 58 -19 21 9 29 8 44 -6 18 -15 21 -15
47 6 27 21 28 21 42 2 14 -19 15 -18 34 5 21 26 18 41 -5 22 -12 -10 -17 -8
-26 8 -14 28 -22 26 -49 -10z"
        />
      </g>
    </svg>
  );
};

/**
 * This component renders a large input field with a button and graphic on it
 * When clicking on it, a modal with a pad will open.
 * Upon submitting the signature, a submit callback is called.
 *
 * @param value: The data URI to render the signature
 * @param required: We do not provide a default value to make sure the decision is made explicitly
 * @param disabled: We do not provide a default value to make sure the decision is made explicitly
 * @param onSubmit: Callback that receives the signature data URI once the user submits it
 * @param errorMessage: Optional error message rendered below the input
 * @param template: Object with width and height props. Template is based on the target PDF Bounding in the PDF Templates, e.g, generatePrequalServiceFormApplication/formTemplate.ts. The Ideal aspect Ratio will be 3/2 for accessibility reasons
 * @param context: Either "DEFAULT" or "SIGNING_CEREMONY". In the signing ceremony, the forms are rendered and filled out directly where the placements are. Often, the placements are relatively small requiring a more condensed signature input.
 * @param dataTestIdOpenModal: ID for e2e tests to identify the signature modal trigger
 * @param dataTestIdCanvas: ID for e2e tests to identify the signature canvas
 * @param dataTestIdSubmit: ID for e2e tests to identify the signature submit button
 * @param dataTestIdPreview: ID for e2e tests to identify the signature preview image
 */
const SignatureInput: FC<{
  value: null | string;
  required: boolean;
  disabled: boolean;
  onSubmit: (signature: string) => Promise<void> | void;
  errorMessage?: string;
  template: {
    width: number;
    height: number;
  };
  context?: 'DEFAULT' | 'SIGNING_CEREMONY';
  dataTestIdOpenModal?: string;
  dataTestIdCanvas?: string;
  dataTestIdSubmit?: string;
  dataTestIdPreview?: string;
  translations: {
    modalTitle: string;
    cancelButton: string;
    submitButton: string;
    signature: string;
    tabToSign: string;
  };
}> = ({
  value,
  required,
  disabled,
  onSubmit,
  errorMessage,
  dataTestIdOpenModal,
  dataTestIdCanvas,
  dataTestIdSubmit,
  dataTestIdPreview,
  template,
  context = 'DEFAULT',
  translations
}) => {
  const [signautreRef, setSignautreRef] = useState<SignatureCanvas | null>(
    null
  );
  const [showPad, setShowPad] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);
  const handleShowPad = () => {
    if (!disabled) {
      setShowPad(true);
    }
  };
  const handleClosePad = () => {
    setShowPad(false);
  };
  const handleEnd = () => {
    setIsEmpty(!!signautreRef?.isEmpty());
  };
  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);
    signautreRef?.off();

    const signatureDataUrl = signautreRef?.toDataURL('image/png');

    if (!signatureDataUrl) {
      throw new Error('No signature found');
    }

    await onSubmit(signatureDataUrl);
    handleClosePad();
    setIsSubmitting(false);
    signautreRef?.on();
  }, [signautreRef, onSubmit]);

  return (
    <Fragment>
      <Modal
        isOpen={showPad}
        onClose={handleClosePad}
        title={translations.modalTitle}
        dataTestId="signature-modal"
      >
        <Fragment>
          <SignatureCanvas
            ref={(ref) => {
              setSignautreRef(ref);
              if (dataTestIdCanvas) {
                ref
                  ?.getCanvas()
                  ?.setAttribute('data-test-id', dataTestIdCanvas);
              }
            }}
            // We don't set background as it needs to be transparent
            penColor="black"
            minWidth={1}
            canvasProps={{
              className: 'sigCanvas border rounded-2xl',
              style: { aspectRatio: `${template.width} / ${template.height}` }
            }}
            onEnd={handleEnd}
          />

          <div className="flex justify-between">
            <Button
              label={translations.cancelButton}
              variant="GHOST"
              onClick={handleClosePad}
              disabled={isSubmitting}
              size="LARGE"
            />

            <Button
              label={translations.submitButton}
              dataTestId={dataTestIdSubmit}
              onClick={() => {
                void handleSubmit();
              }}
              isLoading={isSubmitting}
              disabled={isEmpty}
              size="LARGE"
            />
          </div>
        </Fragment>
      </Modal>

      <div
        className={classNames('flex flex-col', {
          'space-y-spacing-02': context === 'DEFAULT'
        })}
      >
        <div
          className={classNames('flex flex-row items-center', {
            hidden: context === 'SIGNING_CEREMONY'
          })}
        >
          <p className="body-02 text-secondary">{translations.signature}</p>

          {required && <p className="label-02 text-danger">*</p>}
        </div>

        <div
          className={classNames(
            'bg-secondary flex flex-row justify-center rounded-radius-02',
            {
              'cursor-pointer': !value && !disabled,
              'py-spacing-04': context === 'DEFAULT',
              'py-spacing-01': context === 'SIGNING_CEREMONY'
            }
          )}
          onClick={handleShowPad}
          style={
            context === 'SIGNING_CEREMONY'
              ? {
                  width: template.width,
                  height: template.height
                }
              : undefined
          }
          data-test-id={dataTestIdOpenModal}
        >
          {!value && (
            <div className="flex flex-row space-x-spacing-03 items-center">
              <div
                className={classNames('relative icon-interactive', {
                  'size-8 tablet:size-10': context === 'DEFAULT',
                  'size-full': context === 'SIGNING_CEREMONY'
                })}
              >
                <SignatureIconGraphic />
              </div>

              <Button
                // This label is the same throughout the app. Therefore, we are setting it here and not via a prop
                label={translations.tabToSign}
                size="XSMALL"
                className={classNames('tablet:w-48 h-fit', {
                  hidden: context === 'SIGNING_CEREMONY'
                })}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleShowPad();
                }}
              />
            </div>
          )}

          {value && (
            <div className="relative flex justify-center w-full h-8 tablet:h-10">
              <img
                src={value}
                className="h-full object-contain"
                data-test-id={dataTestIdPreview}
              />
            </div>
          )}
        </div>

        <p
          className={classNames(
            'label-02 font-normal text-start min-h-spacing-04',
            {
              'text-danger': Boolean(errorMessage)
            }
          )}
        >
          {errorMessage}
        </p>
      </div>
    </Fragment>
  );
};

export default SignatureInput;
